import { ActionResponse } from '@pickleballinc/lib/server/next/actions/types';

import type { DashboardRead } from '@/app/api/v2/dashboard/read/route';
import { DefaultMiddlewareRes } from '@/utils/helpers/middlewareTypes';

const DASHBOARD_READ_ROUTE = '/api/v2/dashboard/read';

type QueryValue = string | number | boolean | undefined | null;

/**
 * Carries the HTTP status so callers — and the shared react-query retry policy — can tell a
 * refusal from an outage. Without it a 403 on a logged-out viewer is retried like a 503.
 */
export class RouteError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'RouteError';
    this.status = status;
  }
}

async function readBody<T>(response: Response, path: string): Promise<T> {
  // A body that isn't JSON means the failure short-circuited ahead of `defaultResponder` — an
  // ALB/CloudFront 502, an HTML error page, an SSO redirect. Surface the status, not a SyntaxError.
  const body: T | null = await response.json().catch(() => null);

  if (!response.ok || body === null) {
    const envelopeError = (body as { error?: string } | null)?.error;
    throw new RouteError(
      envelopeError ?? `Request to ${path} failed with status ${response.status}`,
      response.status
    );
  }

  return body;
}

/**
 * Client-side read against one of the app's own Route Handlers.
 *
 * Server Actions cannot be used for these: Next funnels every action through a single global
 * client-side queue, so a dashboard full of widgets resolves them strictly one at a time. Plain
 * fetches are parallelised by the browser.
 */
export async function fetchRoute<T>(
  path: string,
  query: Record<string, QueryValue> = {},
  signal?: AbortSignal
): Promise<T | undefined> {
  const searchParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  const url = queryString ? `${path}?${queryString}` : path;
  const body = await readBody<DefaultMiddlewareRes<T>>(await fetch(url, { signal }), path);

  return body.data;
}

/**
 * Client-side read that delegates to a Server Action through the dashboard dispatcher route,
 * for reads whose action is still the single source of truth. Same reason as {@link fetchRoute}:
 * calling the action directly would queue it behind every other action on the page.
 *
 * Resolves only on a 2xx, so a failed read reaches react-query as `isError` rather than as an
 * empty result. The action's envelope is still returned intact for callers that read `error`.
 */
export async function postRoute<T>(
  read: DashboardRead,
  params?: unknown,
  signal?: AbortSignal
): Promise<ActionResponse<T>> {
  const response = await fetch(DASHBOARD_READ_ROUTE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ read, params }),
    signal,
  });

  return readBody<ActionResponse<T>>(response, DASHBOARD_READ_ROUTE);
}
